import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "../components/common";
import { api } from "../lib/api";
import { Seo, SITE_URL, ORG_REF, breadcrumbs } from "../components/Seo";
import { SOLUTIONS } from "../data/content";
import { LANDING } from "../data/landing";

function renderBlock(block, i) {
  if (block.startsWith("## ")) return <h2 key={i} className="text-2xl md:text-3xl font-bold tracking-tight text-white pt-4">{block.slice(3)}</h2>;
  if (block.startsWith("- ")) {
    return (
      <ul key={i} className="space-y-3">
        {block.split("\n").map((li) => <li key={li} className="flex items-start gap-3 text-white/85 text-lg leading-relaxed"><span className="w-1.5 h-1.5 bg-lime mt-3 shrink-0" />{li.replace(/^- /, "")}</li>)}
      </ul>
    );
  }
  return <p key={i} className="text-white/85 text-lg leading-relaxed">{block}</p>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="pt-40 pb-32 container-x mx-auto max-w-[1400px] text-center"><h1 className="text-4xl font-black">Article not found</h1><Link to="/knowledge-hub" className="text-lime mt-6 inline-block">Back to Knowledge Hub</Link></div>;
  if (!post) return <div className="pt-40 pb-32 text-center text-grey">Loading...</div>;
  const cta = SOLUTIONS.find((x) => x.slug === post.cta_slug);

  return (
    <div data-testid="blog-post-page" className="pt-32">
      <Seo
        title={post.title}
        path={`/knowledge-hub/${post.slug}`}
        description={post.excerpt}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Article", headline: post.title, description: post.excerpt, image: post.image, datePublished: post.published_at, dateModified: post.published_at, articleSection: post.category, author: { "@type": "Organization", name: post.author, url: SITE_URL }, publisher: ORG_REF, mainEntityOfPage: `${SITE_URL}/knowledge-hub/${post.slug}`, inLanguage: "en-GB" },
            breadcrumbs([{ name: "Home", path: "/" }, { name: "Knowledge Hub", path: "/knowledge-hub" }, { name: post.title, path: `/knowledge-hub/${post.slug}` }]),
          ],
        }}
      />
      <article className="container-x mx-auto max-w-[820px] py-16">
        <Link to="/knowledge-hub" className="inline-flex items-center gap-2 text-grey hover:text-lime mb-10 text-sm"><ArrowLeft className="w-4 h-4" /> Knowledge Hub</Link>
        <Reveal>
          <span className="text-xs uppercase tracking-widest text-lime">{post.category}</span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mt-4 leading-[1.05]">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-grey mt-6"><span>{post.author}</span><span className="w-1 h-1 bg-grey rounded-full" /><span>{post.read_time}</span></div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="h-72 md:h-96 my-10 overflow-hidden border border-[#808080]/20">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="prose-content space-y-6">
            {post.body.split("\n\n").map((block, i) => renderBlock(block, i))}
          </div>
        </Reveal>
        <div className="mt-16 pt-10 border-t border-[#808080]/20 text-center">
          <h3 className="text-2xl font-bold">{cta ? `Ready to talk about ${cta.title.toLowerCase()}?` : "Need funding for your business?"}</h3>
          <p className="text-grey mt-3 max-w-lg mx-auto">Speak to a DNA Finance adviser. Whole-of-market comparison, no obligation, no impact on your credit score.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            {cta && <Link to={`/solutions/${cta.slug}`} data-testid="blog-solution-cta" className="inline-flex items-center justify-center gap-2 bg-lime text-[#1A1A1A] font-bold px-8 py-4 hover:glow-lime transition-shadow">{LANDING[cta.slug]?.cta || `Get a ${cta.title} Quote`} <ArrowRight className="w-5 h-5" /></Link>}
            <button onClick={() => navigate("/contact?src=blog")} data-testid="blog-cta-btn" className={`inline-flex items-center justify-center gap-2 font-bold px-8 py-4 transition-colors ${cta ? "border border-white/30 hover:border-lime hover:text-lime" : "bg-lime text-[#1A1A1A] hover:glow-lime"}`}>Get Funding {!cta && <ArrowRight className="w-5 h-5" />}</button>
          </div>
        </div>
      </article>
    </div>
  );
}
